import { Fragment, type ReactElement } from "react";
import { PROTOCOL, type Channel } from "@/app/master";
import { ATTEMPT_PILL, attemptVerdict, superseded, type MessageLine } from "@/app/messages";
import { nameOf, type useNames } from "@/app/queries";
import { Button, formatTime, MessageStatePill, Msisdn, Pill, ShortId, type ColWidth } from "@/ui";

/** 消息记录表格可显示的列，按表格里的顺序；“显示列”里勾选显示哪些，缺省全显示，详情按钮列固定不隐藏、吸收剩余宽度（决策 D198）；w 是固定列宽（#541，决策 D199）。 */
export const MESSAGE_COLUMNS = [
  { key: "id", label: "ID", w: "id" }, { key: "created", label: "接收", w: "time" }, { key: "customer", label: "客户", w: 160 }, { key: "account", label: "账号", w: 160 },
  { key: "protocol", label: "协议", w: 72 }, { key: "sender", label: "Sender", w: 120 }, { key: "msisdn", label: "号码", w: 168 }, { key: "mccMnc", label: "MCC/MNC", w: 88 },
  { key: "channel", label: "通道", w: 160 }, { key: "attempts", label: "投递次数", numeric: true, w: 88 }, { key: "segments", label: "分段", numeric: true, w: 64 },
  { key: "status", label: "状态", w: 168 }, { key: "error", label: "错误", w: 160 }, { key: "dlr", label: "客户回执", w: 96 }, { key: "delivered", label: "送达时间", w: "time" },
] as const satisfies readonly { key: string; label: string; numeric?: boolean; w: ColWidth }[];

/** 消息记录表格的列键。 */
export type MessageColumn = (typeof MESSAGE_COLUMNS)[number]["key"];

interface Props extends MessageLine {
  names: ReturnType<typeof useNames>;
  channels?: Channel[];
  /** 显示的列，按 MESSAGE_COLUMNS 的顺序。 */
  columns: readonly MessageColumn[];
  onOpen: (id: string) => void;
}

/** 消息记录表格的一行（#204、#213）：聚合重投时是一条消息，不聚合时是它的一次尝试，通道、次数、状态、错误与送达时间取这一次的（决策 D187）；只画显示的列（决策 D198）。 */
export function MessageLineRow({ m, a, top, names, channels, columns, onOpen }: Props) {
  const channel = a ? a.channel_id : m.channel_id;
  const error = a ? a.upstream_error || attemptVerdict(a) : m.error_code ? `${m.error_code} ${m.error_name}` : "";
  // 只有送达才有送达时间；失败的终态看错误列
  const delivered = a ? (a.status === "DELIVERED" ? a.done_at : null) : m.status === "DELIVERED" ? m.done_at : null;
  const cells: Record<MessageColumn, ReactElement> = {
    id: <td><ShortId id={m.id} /></td>,
    created: <td className="ax-mono">{formatTime(m.created_at)}</td>,
    customer: <td>{names.customer(m.customer_id)}</td>,
    account: <td>{names.account(m.account_id)}</td>,
    protocol: <td>{PROTOCOL[m.protocol]}</td>,
    sender: <td className="ax-mono">{m.source_addr}</td>,
    msisdn: <td><Msisdn value={m.dest_msisdn} /></td>,
    mccMnc: <td className="ax-mono">{m.dest_mcc}/{m.dest_mnc || "*"}</td>,
    channel: <td>{channel == null ? "—" : nameOf(channels, channel)}</td>,
    // 聚合时是投递次数，即最新一次尝试的序号（决策 D62），还没开尝试的是 0；不聚合时是这一次的序号
    attempts: <td className="ax-num">{a ? a.seq_no : m.channel_id == null ? 0 : m.attempt_seq}</td>,
    segments: <td className="ax-num">{m.segments}</td>,
    // 与详情同一基准：已写入的尝试里序号最大的（attempts 从大到小）
    status: <td><MessageStatePill state={a ? ATTEMPT_PILL[a.status] : m.status} />{a && superseded(a, m.attempts?.[0]?.seq_no ?? m.attempt_seq) && <> <Pill tone="warning" plain>已被取代</Pill></>}</td>,
    error: <td className="ax-mono">{error}</td>,
    // 客户回执是消息级的，不聚合时只写在这条消息显示出来的序号最大那行（按通道筛掉最新一次时也不丢）
    dlr: <td className="ax-mono">{top && m.customer_dlr_final_at ? m.customer_dlr_status : ""}</td>,
    delivered: <td className="ax-mono">{delivered ? formatTime(delivered) : ""}</td>,
  };
  return (
    <tr>
      {columns.map((k) => <Fragment key={k}>{cells[k]}</Fragment>)}
      <td><Button variant="ghost" size="sm" onClick={() => onOpen(m.id)}>详情</Button></td>
    </tr>
  );
}
